<adapt-context-sidebar-implementation-kit [articleId]="ImplementationKitArticle.EffectiveStewardship"
                                          context="page"></adapt-context-sidebar-implementation-kit>

<adapt-meetings-filter [team]="team"
                       (meetingFilterChanged)="onMeetingFilterChanged($event)"></adapt-meetings-filter>
<div adaptToolbarContent>
    @if (canEditTeamMeeting) {
        <button adaptButtonIcon="add"
                [adaptBlockingClick]="createTeamMeeting"
                data-test="schedule-meetings-button">Schedule a meeting</button>
    }
</div>
<ng-container *adaptLoading="!isActivated">
    @if (hasMeeting) {
        <adapt-two-column-page>
            <div left-column>
                <!-- no point showing if you can't create or edit meetings -->
                @if (canEditTeamMeeting) {
                    <adapt-meeting-integration-prompt [contentTemplate]="importMeetingPrompt"
                                                      [isDismissible]="true"></adapt-meeting-integration-prompt>
                }
                <ng-template #importMeetingPrompt
                             let-provider
                             let-user="user">
                    @if (provider && provider.id === CalendarIntegrationProvider.Microsoft && !!user) {
                        <button adaptButton="downloadLink"
                                (click)="importExistingOutlookMeeting()">Import existing {{ provider.name }} event</button>
                    }
                </ng-template>
                <dx-scroll-view #meetingListScrollView
                                class="meeting-list-scroll"
                                showScrollbar="always">
                    <div class="d-flex flex-column gap-3">
                        <adapt-team-meeting-guidance-dashboard-element [team]="team"
                                                                       [isCollapsible]="true"></adapt-team-meeting-guidance-dashboard-element>
                        @if (ongoingMeetings.length > 0) {
                            <adapt-dashboard-element name="Ongoing meetings">
                                <dx-list [dataSource]="ongoingMeetings"
                                         selectionMode="single"
                                         (onInitialized)="ongoingMeetingList = $event.component"
                                         (onSelectionChanged)="onSelectionChanged($event)"
                                         (onContentReady)="updateMeetingListSelection()"
                                         (onGroupRendered)="onGroupRendered($event)"
                                         [focusStateEnabled]="false"
                                         class="dx-list-boxed">
                                    <adapt-display-meeting-summary *dxTemplate="let meeting of 'item'"
                                                                   [selected]="selectedMeeting === meeting"
                                                                   (elementSelected)="selectedMeetingElement = $event"
                                                                   [meeting]="meeting"
                                                                   [preWorkClickable]="false"
                                                                   [showIcon]="true"></adapt-display-meeting-summary>
                                </dx-list>
                            </adapt-dashboard-element>
                        }
                        @if (upcomingMeetingsRaw.length > 0) {
                            <adapt-dashboard-element name="Upcoming meetings">
                                <dx-list [dataSource]="upcomingMeetings"
                                         selectionMode="single"
                                         (onInitialized)="upcomingMeetingList = $event.component"
                                         (onSelectionChanged)="onSelectionChanged($event)"
                                         (onContentReady)="updateMeetingListSelection()"
                                         (onGroupRendered)="onGroupRendered($event)"
                                         [focusStateEnabled]="false"
                                         [grouped]="true"
                                         [collapsibleGroups]="true"
                                         class="dx-list-boxed">
                                    <div *dxTemplate="let group of 'group'">
                                        {{ group.key.label }}
                                    </div>
                                    <adapt-display-meeting-summary *dxTemplate="let group of 'item'"
                                                                   [selected]="selectedMeeting === group.meeting"
                                                                   (elementSelected)="selectedMeetingElement = $event"
                                                                   [meeting]="group.meeting"
                                                                   [preWorkClickable]="false"
                                                                   [showIcon]="true"
                                                                   (meetingStarted)="selectedMeeting = group.meeting"></adapt-display-meeting-summary>
                                </dx-list>
                            </adapt-dashboard-element>
                        }
                        @if (endedMeetingsRaw.length > 0) {
                            <adapt-dashboard-element name="Past meetings"
                                                     [info]="pastMeetingInfo">
                                <dx-list [dataSource]="endedMeetings"
                                         selectionMode="single"
                                         (onInitialized)="endedMeetingList = $event.component"
                                         (onSelectionChanged)="onSelectionChanged($event)"
                                         (onContentReady)="updateMeetingListSelection()"
                                         (onGroupRendered)="onGroupRendered($event)"
                                         [focusStateEnabled]="false"
                                         [grouped]="true"
                                         [collapsibleGroups]="true"
                                         [searchEnabled]="true"
                                         searchExpr="meeting.name"
                                         class="dx-list-boxed"
                                         data-test="past-meetings-list">
                                    <dxo-search-editor-options [onInitialized]="onEndedMeetingsListSearchInitialised">
                                    </dxo-search-editor-options>
                                    <div *dxTemplate="let group of 'group'">
                                        {{ group.key.label }}
                                    </div>
                                    <adapt-display-meeting-summary *dxTemplate="let group of 'item'"
                                                                   [selected]="selectedMeeting === group.meeting"
                                                                   (elementSelected)="selectedMeetingElement = $event"
                                                                   [meeting]="group.meeting"
                                                                   [preWorkClickable]="false"
                                                                   [showIcon]="true"></adapt-display-meeting-summary>
                                </dx-list>
                            </adapt-dashboard-element>
                        }
                        @if (hasHiddenMeetings) {
                            @if (!filters) {
                                <ng-container *ngTemplateOutlet="defaultFilterHiddenMeetingsAlert"></ng-container>
                            } @else {
                                <div class="alert alert-secondary">Some meetings are hidden by the current filters.</div>
                            }
                        }
                    </div>
                </dx-scroll-view>
            </div>
            <div right-column>
                @if (selectedMeeting) {
                    <adapt-meeting-details [meeting]="selectedMeeting"
                                           [displayOptions]="filters"
                                           (meetingChange)="updateData$.next()"
                                           (meetingDeleted)="onMeetingDeleted()"></adapt-meeting-details>
                }
            </div>
        </adapt-two-column-page>
    } @else {
        @if (filters && hasHiddenMeetings) {
            <div class="alert alert-info">No meetings match the current filter. Try adjusting your filters to see more meetings.</div>
        } @else {
            <adapt-call-to-action learnMoreUrl="https://support.theadaptway.com/teams/good-team-stewardship">
                <adapt-call-to-action-title>Team meetings</adapt-call-to-action-title>
                <p>A good meeting will have clear guidance. As part of this, facilitate the team to determine what that is and capture on
                    the team dashboard. For each meeting type, they need clarity on the purpose of the meeting.</p>
                <p>The frequency and duration of the meeting will clarify an appropriate cadence and time to devote to the meeting. Guide
                    the team on what is best for each meeting type and try not to let them shorten the meetings – each is really important.
                </p>
                @if (hasHiddenMeetings) {
                    <ng-container *ngTemplateOutlet="defaultFilterHiddenMeetingsAlert"></ng-container>
                }
                <!-- Don't have to check for meetings feature as this page won't be loaded without it -->
                @if (canEditTeamMeeting) {
                    <button adaptCallToActionButton
                            adaptButtonIcon="add"
                            [adaptBlockingClick]="createTeamMeeting">Schedule a meeting</button>
                }
            </adapt-call-to-action>
        }
    }
</ng-container>

<ng-template #defaultFilterHiddenMeetingsAlert>
    <div class="alert alert-info">
        <b>Some meetings are not shown because the default filter excludes meetings older than a year.</b>
        <div class="d-flex">
            <button class="ms-auto btn-sm"
                    adaptButton="primary"
                    (click)="showAllMeetings()">Show all meetings</button>
        </div>
    </div>
</ng-template>
